import type { ReactNode } from 'react';
import styles from './Utterance.module.css';
import { Button } from './Button.js';
import { CharacterAvatar, type CharacterState } from './CharacterAvatar.js';
import { Icon } from './Icon.js';
import { ICONS } from '../icons/index.js';

/**
 * **`AssistantUtterance` / `LearnerBubble`**（§5.5）。
 *
 * ## AI の発話は全幅プレーン（バブルを使わない）
 *
 * §4.1 で ChatGPT / Claude から借りると書いた版面と、
 * **既存の Xlabo プロダクトが独立に一致していた**（§1.1）。
 *
 * ## カーソルは点滅しない（D93）
 *
 * **`generative border` は不採用**（D93 決定6）—
 * **縁は DADS の二重リング（focus）と同じ場所を取り合う**。
 * **行内の 2px の縦棒が同じ役目を果たし、要素の外周を使わない。**
 *
 * ## 入場は最初のトークンでコンテナに 1 回だけ（§5.5）
 *
 * **トークンごとにアニメーションしない** — 文字が増えるたびに動くと読めない。
 */
export type AssistantState = 'waiting' | 'streaming' | 'stalled' | 'done';

export interface AssistantUtteranceProps {
  readonly text: string;
  readonly state: AssistantState;
  /** **名前を出すか**（§4.6 規約 4: **連続発話では繰り返さない**） */
  readonly showName?: boolean;
  /**
   * **エクマルの原本 SVG**（`?raw`）。
   *
   * **省略できる** — 素材を持たない文脈（テスト・断り文）でも組めるようにする。
   * **必須にすると、絵の都合で発話が出せない場面ができる。**
   */
  readonly avatarSvg?: string;
  /**
   * **アプリが決定的に決めた表情**（§4.6 規約 6。**LLM に選ばせない**）。
   *
   * **正解への近さを表情で伝えない**（規約 7）— これは INV-5 の漏洩経路であり、
   * **`displayLabel` の検査では捕まらない**。
   */
  readonly character?: CharacterState;
  /**
   * **異議申し立ての入口**（§4.8 / D22。**再生成ボタンの位置を占める**）。
   * **渡さなければ出さない** — probe や断りには要らない。
   */
  readonly onObject?: () => void;
}

export function AssistantUtterance({
  text,
  state,
  showName = true,
  avatarSvg,
  character,
  onObject,
}: AssistantUtteranceProps): ReactNode {
  return (
    <div className={styles['assistantBlock']}>
      {showName ? (
        <div className={styles['who']}>
          {avatarSvg === undefined ? null : (
            <CharacterAvatar svg={avatarSvg} {...(character === undefined ? {} : { state: character })} />
          )}
          <span className={styles['whoName']}>エクマル</span>
        </div>
      ) : null}
      <div className={styles['assistant']}>
        {text}
        {/* **流れている間だけカーソルを出す**（`done` では消す） */}
        {state === 'streaming' ? <span className={styles['cursor']} aria-hidden="true" /> : null}
      </div>
      {onObject === undefined ? null : (
        <div className={styles['actions']}>
          {/**
           * **「気に入らない」ではなく「この診断は違う」を受ける**（§4.8）。
           * **`subtle` にする** — 主操作ではなく、**必要な人が見つけられればよい**。
           */}
          <Button variant="subtle" size="sm" onClick={onObject}>
            この指摘は違うと思う
          </Button>
        </div>
      )}
    </div>
  );
}

export type LearnerState = 'sending' | 'sent' | 'failed';

export interface LearnerBubbleProps {
  readonly text: string;
  readonly state: LearnerState;
}

/** 状態を**色だけで示さない**（文言を添える。§2.6 規約3 と同じ思想） */
const LEARNER_META: Readonly<Record<LearnerState, string | undefined>> = {
  sending: '送信中',
  sent: undefined,
  failed: '送信できませんでした',
};

export function LearnerBubble({ text, state }: LearnerBubbleProps): ReactNode {
  const meta = LEARNER_META[state];
  return (
    <div className={styles['learnerRow']}>
      <div>
        {/** **名前は右寄せ**（モック）。バブルの向きと揃える */}
        <div className={styles['learnerWho']}>
          <Icon svg={ICONS.me} size={16} />
          わたし
        </div>
        <div
          className={[
            styles['learner'],
            state === 'sending' ? styles['pending'] : undefined,
            state === 'failed' ? styles['failed'] : undefined,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {text}
        </div>
        {meta === undefined ? null : <div className={styles['meta']}>{meta}</div>}
      </div>
    </div>
  );
}
